import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { customerAPI, API_BASE_URL, adminCommandsAPI } from '../services/api';
import AddressAutocomplete from './AddressAutocomplete';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  /** userId -> high-temp setpoint draft string for admin MQTT publish */
  const [systemSetpointDrafts, setSystemSetpointDrafts] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    location: '',
    pumpExists: true,
    isActive: true,
  });
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Load customers
  const loadCustomers = async (page = 0, search = '') => {
    setLoading(true);
    setError('');
    
    try {
      const response = await customerAPI.getCustomers(
        page + 1,
        pagination.pageSize,
        search
      );
      
      setCustomers(response.users);
      setPagination(prev => ({
        ...prev,
        page,
        total: response.pagination.total,
      }));
    } catch (err) {
      setError(
        'Failed to load customers: ' + String(err?.response?.data?.message ?? err?.message ?? err)
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    // Real-time PLC status via SSE
    const token = localStorage.getItem('adminToken');
    if (token) {
      const url = `${API_BASE_URL}/admin/heartbeats/sse?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      const applyUpdate = (username, online) => {
        setCustomers(prev => prev.map(c => (c.mqttUsername === username ? { ...c, online } : c)));
      };
      es.addEventListener('snapshot', (e) => {
        try {
          const data = JSON.parse(e.data); // [{ username, online }]
          setCustomers(prev => {
            const map = new Map(prev.map(c => [c.mqttUsername, c]));
            for (const row of data) {
              const c = map.get(row.username);
              if (c) {
                map.set(row.username, { ...c, online: row.online });
              }
            }
            return Array.from(map.values());
          });
        } catch (_) {}
      });
      es.addEventListener('update', (e) => {
        try {
          const data = JSON.parse(e.data); // { username, online }\n
          applyUpdate(data.username, data.online);
        } catch (_) {}
      });
      es.onerror = () => {
        // let the browser auto-reconnect\n
      };
      return () => es.close();
    }
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle dialog open/close
  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        email: customer.email,
        password: '',
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.phone || '',
        address: customer.address || '',
        location: customer.location || '',
        pumpExists: customer.pumpExists ?? true,
        isActive: customer.isActive,
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        location: '',
        pumpExists: true,
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCustomer(null);
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      address: '',
      isActive: true,
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingCustomer) {
        // Update existing customer
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if empty
        }
        await customerAPI.updateCustomer(editingCustomer.id, updateData);
        setSuccess('Customer updated successfully');
      } else {
        // Create new customer
        await customerAPI.createCustomer(formData);
        setSuccess('Customer created successfully');
      }
      
      handleCloseDialog();
      loadCustomers(pagination.page, searchTerm);
    } catch (err) {
      setError(
        editingCustomer 
          ? 'Failed to update customer: ' + String(err?.response?.data?.message ?? err?.message ?? err)
          : 'Failed to create customer: ' + String(err?.response?.data?.message ?? err?.message ?? err)
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle delete customer
  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(`Are you sure you want to delete customer "${customer.firstName} ${customer.lastName}"?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await customerAPI.deleteCustomer(customer.id);
      setSuccess('Customer deleted successfully');
      loadCustomers(pagination.page, searchTerm);
    } catch (err) {
      setError(
        'Failed to delete customer: ' + String(err?.response?.data?.message ?? err?.message ?? err)
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    loadCustomers(0, value);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    loadCustomers(newPage, searchTerm);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize }));
    loadCustomers(0, searchTerm);
  };

  // Define columns for DataGrid
  const columns = [
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 150,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 150,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
    },
    {
      field: 'address',
      headerName: 'Address',
      width: 240,
      renderCell: (params) => (
        <span title={params.value || ''}>{params.value || ''}</span>
      ),
    },
    {
      field: 'mqttUsername',
      headerName: 'MQTT Username',
      width: 200,
    },
    {
      field: 'online',
      headerName: 'PLC',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <CheckCircleIcon /> : <CancelIcon />}
          label={params.value ? 'Online' : 'Offline'}
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 180,
      renderCell: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'hiTempSetpoint',
      headerName: 'Hi-Temp setpoint',
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="High temperature threshold (°F). MQTT: system_setpoint">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              width: '100%',
              height: '100%',
              py: 0.5,
            }}
          >
            <TextField
              size="small"
              type="number"
              placeholder="°F"
              value={String(systemSetpointDrafts[params.row.id] ?? '')}
              onChange={(e) =>
                setSystemSetpointDrafts((prev) => ({
                  ...prev,
                  [params.row.id]: e.target.value,
                }))
              }
              sx={{ width: 88 }}
              inputProps={{ step: '1', 'aria-label': 'High temperature setpoint' }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={async () => {
                const raw = systemSetpointDrafts[params.row.id];
                const n = raw !== undefined && raw !== '' ? Number(raw) : NaN;
                if (!Number.isFinite(n)) {
                  setError('Enter a numeric high temperature setpoint.');
                  return;
                }
                try {
                  setError('');
                  setSuccess('');
                  await adminCommandsAPI.setSystemSetpoint(params.row.id, n);
                  setSuccess(`System setpoint ${n} published for ${params.row.mqttUsername}`);
                } catch (err) {
                  setError(
                    'Failed to publish setpoint: ' +
                      String(err?.response?.data?.message || err?.message || err)
                  );
                }
              }}
            >
              Set
            </Button>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 320,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteCustomer(params.row)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Enable System">
            <span>
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={async () => {
                  try {
                    setError('');
                    setSuccess('');
                    await adminCommandsAPI.enableSystem(params.row.id);
                    setSuccess(`Enable command sent for ${params.row.mqttUsername}`);
                  } catch (err) {
                    setError(
                      'Failed to send enable command: ' +
                        String(err?.response?.data?.message ?? err?.message ?? err)
                    );
                  }
                }}
              >
                Enable
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Disable System">
            <span>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={async () => {
                  try {
                    setError('');
                    setSuccess('');
                    await adminCommandsAPI.disableSystem(params.row.id);
                    setSuccess(`Disable command sent for ${params.row.mqttUsername}`);
                  } catch (err) {
                    setError(
                      'Failed to send disable command: ' +
                        String(err?.response?.data?.message ?? err?.message ?? err)
                    );
                  }
                }}
              >
                Disable
              </Button>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Customer Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadCustomers(pagination.page, searchTerm)}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search customers..."
          value={searchTerm}
          onChange={handleSearch}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      <Box sx={{ height: 600, width: '100%', overflow: 'auto' }}>
        <DataGrid
          rows={customers}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          page={pagination.page}
          pageSize={pagination.pageSize}
          rowCount={pagination.total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          initialState={{ columns: { columnVisibilityModel: { isActive: false } } }}
          sx={{ minWidth: 1280 }}
        />
      </Box>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              required={!editingCustomer}
              helperText={editingCustomer ? "Leave empty to keep current password" : ""}
            />
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              margin="normal"
            />
            <AddressAutocomplete
              label="Address"
              value={formData.address}
              onSelect={({ address, location }) => setFormData(prev => ({ ...prev, address, location }))}
              onManualChange={(addr) => setFormData(prev => ({ ...prev, address: addr, location: '' }))}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Typography variant="body2" sx={{ mr: 2 }}>Pump Exists</Typography>
              <Switch
                checked={!!formData.pumpExists}
                onChange={(e) => setFormData(prev => ({ ...prev, pumpExists: e.target.checked }))}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : (editingCustomer ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default CustomerManagement;
